import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pushSupported, currentPermission, isSubscribed, subscribeToPush, unsubscribeFromPush, formatApiError } from "@/lib/pushNotifications";
import { toast } from "sonner";

/**
 * Compact "Enable notifications" button.
 * Hidden if the browser doesn't support push. Uses toast for feedback.
 */
export function PushNotificationsButton({ compact = false }) {
  const [supported, setSupported] = useState(false);
  const [subbed, setSubbed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [perm, setPerm] = useState("default");

  useEffect(() => {
    const init = async () => {
      const s = pushSupported();
      setSupported(s);
      if (s) {
        setPerm(await currentPermission());
        setSubbed(await isSubscribed());
      }
    };
    init();
  }, []);

  if (!supported) return null;

  const toggle = async () => {
    setBusy(true);
    try {
      if (subbed) {
        await unsubscribeFromPush();
        toast.success("Notifiche disattivate");
        setSubbed(false);
      } else {
        await subscribeToPush();
        toast.success("Notifiche attivate: ti avviseremo per i nuovi ticket assegnati");
        setSubbed(true);
        setPerm("granted");
      }
    } catch (e) {
      toast.error(e?.message || formatApiError(e));
      setPerm(await currentPermission());
    } finally { setBusy(false); }
  };

  if (perm === "denied") {
    return (
      <div className={`text-[10px] text-zinc-500 ${compact ? "" : "px-2"}`}>
        <BellOff className="h-3 w-3 inline mr-1" />
        Notifiche bloccate nel browser
      </div>
    );
  }

  return (
    <Button
      data-testid="push-toggle-btn"
      variant="outline"
      size="sm"
      onClick={toggle}
      disabled={busy}
      className={`rounded-sm ${compact ? "h-8 w-full" : "w-full"}`}
      title={subbed ? "Disattiva notifiche" : "Attiva notifiche per i ticket assegnati"}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : subbed ? <Bell className="h-3.5 w-3.5 mr-2 text-emerald-600" /> : <BellOff className="h-3.5 w-3.5 mr-2" />}
      <span className="text-xs">{subbed ? "Notifiche attive" : "Attiva notifiche"}</span>
    </Button>
  );
}
